// ...existing code...
import AuthLayout from 'components/AuthLayout/AuthLayout'
import { replace } from 'connected-react-router'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Typography, Button as AntdButton, Space, message, Card, Radio, Form, Empty, Tooltip } from 'antd'
import OTPInput from 'components/OTP'
import { Button } from 'components/Buttons/Buttons'
import { resendTfaCode, verifyTfaCode } from 'services/auth.services'
import { set } from 'lodash'
import Modal from 'components/Modal/Modal'
import { FormRadio, FormRadioGroup } from 'components/FormItems/FlatFormItems'


const { Title, Text } = Typography
const codeLength = 6

export default function VerifyTFA() {
    const dispatch = useDispatch()
    const { tfaTemporaryToken, tfaExpiry, tfaRequired, tfaIsEmailConfirmed, tfaIsPhoneNumberConfirmed, tfaVerified, tfaEmail, tfaPhone = "", tfaPreferredVerificationMode, user, loading } = useSelector(state => ({
        tfaTemporaryToken: state.user.tfaTemporaryToken,
        tfaRequired: state.user.tfaRequired,
        tfaVerified: state.user.tfaVerified,
        tfaEmail: state.user.tfaEmail,
        tfaPhone: state.user.tfaPhone,
        tfaPreferredVerificationMode: state.user.tfaPreferredVerificationMode,
        user: state.user.data,
        loading: state.app.loading,
        tfaExpiry: state.user.tfaExpiry,
        tfaIsEmailConfirmed: state.user.tfaIsEmailConfirmed,
        tfaIsPhoneNumberConfirmed: state.user.tfaIsPhoneNumberConfirmed
    }))

    // default to preferred mode from store; will update if store changes
    const [selectedTFAMethod, setSelectedTFAMethod] = useState(tfaPreferredVerificationMode ?? (tfaIsEmailConfirmed ? 1 : 0))
    const [code, setCode] = useState('')
    const [secondsLeft, setSecondsLeft] = useState()
    const [sending, setSending] = useState(false)
    const [verifying, setVerifying] = useState(false)
    const [showTFAMethodSelectionPopup, setShowTFAMethodSelectionPopup] = useState(false)

    const intervalRef = useRef(null)
    const expiryIntervalRef = useRef(null)
    const mountedRef = useRef(true)

    // ensure selectedTFAMethod tracks store changes and available fallback
    useEffect(() => {
        const fallback = tfaIsEmailConfirmed ? 1 : (tfaIsPhoneNumberConfirmed ? 0 : undefined)
        setSelectedTFAMethod(typeof tfaPreferredVerificationMode !== 'undefined' && tfaPreferredVerificationMode !== null
            ? tfaPreferredVerificationMode
            : fallback)
    }, [tfaPreferredVerificationMode, tfaIsEmailConfirmed, tfaIsPhoneNumberConfirmed])

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
            if (expiryIntervalRef.current) {
                clearInterval(expiryIntervalRef.current)
                expiryIntervalRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        startTimer()
    }, [])

    const availableMethods = useMemo(() => {
        const opts = []
        if (tfaEmail) opts.push({ label: 'Email', value: 1 })
        if (tfaPhone) opts.push({ label: 'Phone', value: 0 })
        return opts
    }, [tfaIsEmailConfirmed, tfaEmail, tfaIsPhoneNumberConfirmed, tfaPhone])

    useEffect(() => {
        // if there's no temp token go back to login
        if (!tfaTemporaryToken) {
            dispatch(replace('/'))
            return
        }
        // if already verified redirect
        if (tfaVerified) {
            dispatch(replace('/'))
            return
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tfaTemporaryToken])

    useEffect(() => {
        // redirect if verification state flips true elsewhere
        if (tfaVerified) {
            dispatch(replace('/'))
        }
    }, [tfaVerified, dispatch])

    const verificationTarget = useMemo(() => {
        if (selectedTFAMethod == 1) {
            return tfaEmail
        } else if (selectedTFAMethod == 0 && tfaPhone) {
            let digits = tfaPhone.replace(/\D/g, "");

            // remove leading country code 1 if present
            if (digits.length === 11 && digits.startsWith("1")) {
                digits = digits.slice(1);
            }

            if (digits.length !== 10) return "";

            return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        }
        return "account"

    }, [tfaPhone, tfaEmail, selectedTFAMethod])

    const startTimer = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }
        const seconds = Math.max(0, Math.floor((tfaExpiry || 0) * 60))
        setSecondsLeft(seconds)
        intervalRef.current = setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) {
                    clearInterval(intervalRef.current)
                    intervalRef.current = null
                    return 0
                }
                return s - 1
            })
        }, 1000)
    }

    // allow resend only after 1 minute from issuance has elapsed (elapsed >= 60) or when expired
    const canResend = () => {
        const total = Math.max(0, Math.floor((tfaExpiry || 0) * 60))
        if (!total) return true
        const elapsed = total - (secondsLeft ?? 0)
        return elapsed >= 60 || (secondsLeft === 0)
    }

    const doSend = async (method = selectedTFAMethod) => {
        setCode('')
        if (!tfaTemporaryToken) return
        setSending(true)
        try {
            // pass the chosen method to resendTfaCode
            const res = await dispatch(resendTfaCode(tfaTemporaryToken, method))
            if (res?.status === 'success') {
                startTimer()
            }
        } catch (err) {
            console.error(err)
        } finally {
            if (mountedRef.current) setSending(false)
        }
    }

    const resendTooltip = () => {
        const total = Math.max(0, Math.floor((tfaExpiry || 0) * 60))
        if (!total) return ''
        const elapsed = total - (secondsLeft ?? 0)
        const remaining = Math.max(0, 60 - elapsed)
        return remaining > 0 ? `New code can only be requested after ${remaining} seconds` : ''
    }

    const doVerify = async () => {
        if (!tfaTemporaryToken) return
        if (code.length !== codeLength) return
        setVerifying(true)
        try {
            await dispatch(verifyTfaCode(tfaTemporaryToken, code, selectedTFAMethod))
        } catch (err) {
            console.error(err)
            message.error(err?.message || 'Invalid code')
        } finally {
            if (mountedRef.current) setVerifying(false)
        }
    }

    const formattedTimer = () => {
        const m = Math.floor((secondsLeft ?? 0) / 60)
        const s = (secondsLeft ?? 0) % 60
        return `${m}:${String(s).padStart(2, '0')}`
    }

    const formattedTime = formattedTimer()

    return (
        <AuthLayout loading={loading} message={""} logoSizeSmall={true}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={3} style={{ margin: 0 }}>
                        Two-Factor Authentication
                    </Title>

                    <Text>
                        Enter the {codeLength}-digit code sent to <Text strong>{verificationTarget}</Text>
                    </Text>

                    <Form onFinish={verifying ? () => { } : doVerify}>
                        <Row justify="center">
                            <OTPInput
                                length={codeLength}
                                value={code}
                                onChange={(v) => setCode(v)}
                                autoFocus
                            />
                        </Row>
                        <button type={verifying ? "button" : "submit"} hidden>Submit</button>
                    </Form>
                    {
                        !!secondsLeft && <Row>
                            <Typography.Text>
                                This code will expire in {formattedTime}
                            </Typography.Text>
                        </Row>
                    }
                    {
                        !secondsLeft && <Row>
                            <Typography.Text>
                                This code has expired. Please resend to get a new code.
                            </Typography.Text>
                        </Row>
                    }


                    <Row justify="center">
                        <div style={{ marginTop: 16 }}>
                            <Typography.Text style={{ color: '#666', fontSize: 14, }}>
                                Didn't receive the code?{' '}
                                <Tooltip title={resendTooltip()}>
                                    <AntdButton
                                        type='text'
                                        onClick={() => {
                                            if (!canResend()) return
                                            doSend(selectedTFAMethod)
                                        }}
                                        disabled={!canResend() || sending}
                                        style={{ margin: 0, padding: 0 }}
                                    >
                                        {sending ? 'Sending...' : 'Resend'}
                                    </AntdButton>
                                </Tooltip>
                            </Typography.Text>
                        </div>

                        <div style={{ marginLeft: 16 }}>
                            <Typography.Text style={{ color: '#666', fontSize: 14 }}>
                                Or try a&nbsp;
                            </Typography.Text>
                            <AntdButton type='text' style={{ padding: 0, margin: 0 }} onClick={() => setShowTFAMethodSelectionPopup(true)}>  different method</AntdButton>
                        </div>
                    </Row>
                </Space>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                    <AntdButton type="text" style={{ padding: 0 }} onClick={() => dispatch(replace('/'))}>Cancel</AntdButton>
                    <Button
                        rounded
                        type="primary"
                        onClick={doVerify}
                        loading={verifying}
                    >
                        Verify
                    </Button>
                </div>
            </Card>
            <Modal
                title="Available Methods"
                closable={true}
                destroyOnClose={true}
                visible={showTFAMethodSelectionPopup}
                onCancel={() => setShowTFAMethodSelectionPopup(false)}
                footer={<>
                </>}
                width={420}
            >
                {
                    availableMethods?.length > 0 ?
                        <Form layout='vertical'
                            scrollToFirstError={true}
                            onFinish={(values) => {
                                const selectedMethod = Number(values.tfamethod)
                                setSelectedTFAMethod(selectedMethod)
                                doSend(selectedMethod)
                                setShowTFAMethodSelectionPopup(false)
                            }}
                        >
                            <div style={{ display: 'flex' }}>

                                <FormRadioGroup
                                    name="tfamethod"
                                    inline={false}
                                    vertical={true}
                                    required={true}
                                    options={availableMethods?.map(option => ({
                                        value: option.value,
                                        label: `${option.label} ${selectedTFAMethod === option.value ? "(selected)" : ""}`,
                                        disabled: option.value === selectedTFAMethod
                                    }))}
                                />
                            </div>
                            <Button type='submit' htmlType={'submit'} rounded>Send</Button>
                        </Form> :
                        <Empty description="No other available methods" />
                }

            </Modal>
        </AuthLayout>
    )
}